import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Loading from './Loading';
import { supabase } from '../supabaseClient';

interface RequireAuthProps {
  children: React.ReactElement;
  redirectTo?: string;
}

const RequireAuth: React.FC<RequireAuthProps> = ({ children, redirectTo }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [delayRedirect, setDelayRedirect] = useState(true);
  const [checkingSession, setCheckingSession] = useState(false);
  const [sessionUserFound, setSessionUserFound] = useState<any>(null);
  const [userProfile, setUserProfile] = useState<any | null>(null);
  const [checkingUserTipo, setCheckingUserTipo] = useState(false);

  // Al cambiar de ruta, damos un pequeño margen para que AuthProvider
  // reciba la sesión de Supabase antes de decidir que no hay user.
  useEffect(() => {
    setDelayRedirect(true);
    const t = setTimeout(() => setDelayRedirect(false), 2000);
    return () => clearTimeout(t);
  }, [location.pathname]);

  // Si el contexto aún no tiene user, intentamos leer sesión directamente
  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      if (user || checkingSession) return;
      setCheckingSession(true);
      console.debug('RequireAuth: checking direct session because user in context is null, path =', location.pathname);
      const start = Date.now();
      let found: any = null;
      while (!cancelled && Date.now() - start < 3500) {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          found = session.user;
          break;
        }
        await new Promise(r => setTimeout(r, 150));
      }
      if (!cancelled) {
        setSessionUserFound(found);
        setCheckingSession(false);
        console.debug('RequireAuth: direct session check finished, found =', !!found);
      }
    };
    if (!user) {
      check();
    } else {
      setSessionUserFound(null);
      setCheckingSession(false);
    }
    return () => { cancelled = true; };
  }, [user, location.pathname, checkingSession]);

  useEffect(() => {
    let cancelled = false;
    const fetchProfile = async () => {
      if (!user) {
        setUserProfile(null);
        return;
      }
      setCheckingUserTipo(true);
      console.debug('RequireAuth: fetching userProfile for user', user.id, 'path =', location.pathname);
      let { data } = await supabase
        .from('usuarios')
        .select('tipo')
        .eq('id', user.id)
        .maybeSingle();

      if (!data && user.email) {
        const { data: byEmail } = await supabase
          .from('usuarios')
          .select('tipo')
          .eq('email', user.email)
          .maybeSingle();
        data = byEmail || null;
      }
      if (!cancelled) {
        setUserProfile(data || null);
        setCheckingUserTipo(false);
        console.debug('RequireAuth: userProfile loaded =', data);
      }
    };
    fetchProfile();
    return () => {
      cancelled = true;
    };
  }, [user]);

  if (loading) {
    return <Loading />;
  }

  if (!user) {
    if (delayRedirect || checkingSession) {
      return <Loading />;
    }
    if (sessionUserFound) {
      // Hay sesión directa aunque el contexto aún no se actualizó; permitimos el acceso
      return children;
    }
    let target = redirectTo;
    if (!target) {
      if (location.pathname.startsWith('/soy-niva')) {
        target = '/soy-niva/login';
      } else if (location.pathname.startsWith('/busco-niva')) {
        target = '/busco-niva/login';
      } else {
        target = '/seleccion';
      }
    }

    console.debug('RequireAuth: no user, redirecting to', target, 'from', location.pathname, 'sessionUserFound =', !!sessionUserFound);
    return <Navigate to={target} replace />;
  }

  if (checkingUserTipo) {
    return <Loading />;
  }

  const path = location.pathname;

  if (userProfile && userProfile.tipo) {
    if (path.startsWith('/soy-niva') && userProfile.tipo !== 'iyire') {
      console.debug('RequireAuth: user tipo', userProfile.tipo, 'no puede acceder a', path, '-> redirect /busco-niva/perfiles');
      return <Navigate to="/busco-niva/perfiles" replace />;
    }
    if (path.startsWith('/busco-niva') && userProfile.tipo !== 'empleador') {
      console.debug('RequireAuth: user tipo', userProfile.tipo, 'no puede acceder a', path, '-> redirect /soy-niva/mapa');
      return <Navigate to="/soy-niva/mapa" replace />;
    }
  }

  return children;
};

export default RequireAuth;
